import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { getCanonicalTitle } from "../../../common/util/Incentives/IncentiveRegistry";
import { useAppDispatch, useAppSelector } from "../../../store";
import { buildBaseExportPayload, isExportDisabledBySessionSsoOrigin } from "../../../common/util/exportUtil";
import { useFsiActionReportExportV2Mutation } from "../../../services/ExportV2/exportV2API";
import type { TextOverride } from "../../../services/ExportV2/exportV2API.types";
import {
  MODULE_FSI_ACTION_REPORT,
  MODULE_FSI_ACTION_REPORT_IN_FSI,
  MODULE_FSI_ACTION_REPORT_OUT_FSI,
} from "../../../common/constants/export-constants";
import serverSideExportContent from "../../../content/serverSideExport";
import ServerSideExportInProgressNotify from "../../ServerSideExport/ServerSideExportCommon/ServerSideExportNotify/ServerSideExportInProgressNotify/ServerSideExportInProgressNotify";
import ServerSideExportCompletedNotify from "../../ServerSideExport/ServerSideExportCommon/ServerSideExportNotify/ServerSideExportCompletedNotify/ServerSideExportCompletedNotify";
import { dispatchOpenExportV2ModalEvent } from "../../ServerSideExport/ServerSideExportCommon/ServerSideExportUtil/ServerSideExportEvents";
import { getDefaultHomePage } from "../../../common/util/Navigation/navigationUtil";
import { BonusInfoFooter } from "../../../common/components/BonusInfoFooter/BonusInfoFooter";
import { ActionReportHeader } from "../common/Header/ActionReportHeader";
import { ActionReportToolbar } from "../common/Toolbar/ActionReportToolbar";
import { ActionReportListView, ActionReportListViewLoader } from "../common/ActionReportListView/ActionReportListView";
import type { BaseABOData } from "../common/ActionReportListView/ActionReportListView";
import type { FieldConfig } from "../common/ActionReportListView/types";
import LeafRectangleLoader from "../../../common/leaf/LeafLoadingSkeleton/LeafRectangleLoader/LeafRectangleLoader";
import { ERROR_TYPE, ErrorPage, IconType } from "../../ErrorPage/ErrorPage";
import { FetchFsiActionReportData } from "./FsiActionReportDataLayer";
import type { FsiTrackingDownline } from "../../../services/ActionReports/ActionReportAPI.types";
import { storedLocale } from "../../Authentication/util";
import { getFullMonth } from "../../../common/util/period";
import defaultContent from "../../../content/actionReportsFsiTracking";
import defaultCommonContent from "../../../content/common";
import {
  useGetSavedKpiPreferenceQuery,
  useUpdateSavedKpiPreferenceMutation,
} from "../../../services/KPIPreferences/savedKpiPreferencesAPI";
import { setSavedKpiPreferencesData } from "../../../reducers/savedKpiPreferences";
import {
  getSavedSortPreferenceForView,
  updateSavedSortPreferenceForView,
} from "../../../common/util/savedKpiPrefrencesUtil";
import type {
  SearchSelectItem,
  SearchCallback,
  SelectionCallback,
} from "../../../common/components/SearchSelect/SearchSelect";
import {
  fsiPageViewTrack,
  fsiTabClickTrack,
  fsiProfileTrack,
  fsiAddToWatchlistTrack,
  fsiRemoveFromWatchlistTrack,
  fsiPhoneIconTrack,
  fsiMessageIconTrack,
  fsiSearchTrack,
  fsiExportTrack,
  fsiSortColumnTrack,
  fsiSortButtonTrack,
} from "../../../common/tealium/ActionReports/FsiTrack";
import "../ActionReports.scss";
import "./FsiActionReport.scss";

export type FsiTab = "inFsi" | "outFsi";
type SortDirection = "asc" | "desc";
type TabSortState = Record<string, SortDirection | undefined>;

export const createFsiFieldConfig = (tableColumns: Record<string, string>, activeTab: FsiTab): FieldConfig[] => {
  const base: FieldConfig[] = [
    { id: "aboName", kpi: "aboName", title: tableColumns["aboName"], sortable: true },
    { id: "ppvActual", kpi: "ppvActual", title: tableColumns["ppvActual"], sortable: true },
    { id: "ppvNeeded", kpi: "ppvNeeded", title: tableColumns["ppvNeeded"], sortable: true },
    { id: "vcsPvActual", kpi: "vcsPvActual", title: tableColumns["vcsPvActual"], sortable: true },
    { id: "vcsPvNeeded", kpi: "vcsPvNeeded", title: tableColumns["vcsPvNeeded"], sortable: true },
    { id: "vcsPercent", kpi: "vcsPercent", title: tableColumns["vcsPercent"], sortable: true },
  ];
  if (activeTab === "inFsi") {
    base.push({
      id: "accumulatedVcsBv",
      kpi: "accumulatedVcsBv",
      title: tableColumns["accumulatedVcsBv"],
      sortable: true,
    });
  } else {
    base.push({ id: "exitMonth", kpi: "exitMonth", title: tableColumns["exitMonth"], sortable: true });
  }
  base.push({ id: "phoneNumber", kpi: "phoneNumber", title: "" }, { id: "imageRefUrl", kpi: "imageRefUrl", title: "" });
  return base;
};

// accumulatedVcsBv (In FSI) and exitMonth (Out of FSI) only exist on one tab each —
// used to avoid restoring a saved sort preference onto a tab whose fieldConfig doesn't have that column.
export const isActiveSortValidForTab = (
  columnSortState: Record<string, SortDirection | undefined>,
  fieldConfig: FieldConfig[],
): boolean => {
  const activeColumn = Object.keys(columnSortState).find((key) => columnSortState[key]);
  if (!activeColumn) return true;
  return fieldConfig.some((field) => field.kpi === activeColumn);
};

// accumulatedVcsBv and exitMonth only exist on one tab each, so choosing one only applies to
// the tab it was chosen on and resets the other tab back to default (it's held only across
// plain tab switches, not across a new sort pick made anywhere else).
// Every other sort column is shared by both tabs, so choosing it applies to both at once.
const TAB_EXCLUSIVE_SORT_KPIS: ReadonlySet<string> = new Set(["accumulatedVcsBv", "exitMonth"]);

export const getNextColumnSortState = (
  columnSortState: Record<FsiTab, TabSortState>,
  activeTab: FsiTab,
  kpi: string,
  direction: SortDirection,
): Record<FsiTab, TabSortState> => {
  const nextSort: TabSortState = { [kpi]: direction };
  if (TAB_EXCLUSIVE_SORT_KPIS.has(kpi)) {
    const otherTab: FsiTab = activeTab === "inFsi" ? "outFsi" : "inFsi";
    return { ...columnSortState, [activeTab]: nextSort, [otherTab]: {} };
  }
  return { inFsi: nextSort, outFsi: nextSort };
};

const SELECTED_KPIS: Record<FsiTab, string[]> = {
  inFsi: ["ppvActual", "ppvNeeded", "vcsPvActual", "vcsPvNeeded", "vcsPercent", "accumulatedVcsBv"],
  outFsi: ["ppvActual", "ppvNeeded", "vcsPvActual", "vcsPvNeeded", "vcsPercent", "exitMonth"],
};

const FSI_SORT_VIEW_NAME = "fsiActionReport";

export default function FsiActionReport() {
  const { t } = useTranslation(["actionReportsFsiTracking", "actionReports", "common"]);
  const navigate = useNavigate();
  const { selectedPeriod } = useAppSelector((state) => state.period);
  const boot = useAppSelector((state) => state.boot);
  const { configuration } = boot;
  const { los: isExportDisabled } = isExportDisabledBySessionSsoOrigin();
  const dispatch = useAppDispatch();
  const { savedKpiPreferencesData } = useAppSelector((state) => state.savedKpiPreferencesData);
  const [updateSavedKpiPreference] = useUpdateSavedKpiPreferenceMutation();

  const disclaimerSymbol = t("disclaimerSymbol", defaultCommonContent["disclaimerSymbol"], { ns: "common" });

  const isEligible = configuration.navigation?.showFsiActionReport ?? false;

  const { isLoading, fsiData, errorStatus } = FetchFsiActionReportData(selectedPeriod);

  const [activeTab, setActiveTab] = useState<FsiTab>("inFsi");

  const [columnSortState, setColumnSortState] = useState<Record<FsiTab, TabSortState>>({
    inFsi: {},
    outFsi: {},
  });
  const [searchText, setSearchText] = useState<string>("");
  const [scrollToAbo, setScrollToAbo] = useState<{ aff: string; abo: string } | undefined>(undefined);

  const { isSuccess: savedPreferencesFetchSucceeded, isError: savedPreferencesFetchErrored } =
    useGetSavedKpiPreferenceQuery(FSI_SORT_VIEW_NAME, { refetchOnMountOrArgChange: true });
  const savedPreferencesReady =
    Boolean(savedKpiPreferencesData?.savedDataViews?.[FSI_SORT_VIEW_NAME]) ||
    savedPreferencesFetchSucceeded ||
    savedPreferencesFetchErrored;

  useEffect(() => {
    if (!isLoading && !isEligible) {
      navigate(getDefaultHomePage(configuration.cppModHomePage));
    }
  }, [isLoading, isEligible, navigate, configuration.cppModHomePage]);

  const [pageViewFired, setPageViewFired] = useState(false);
  useEffect(() => {
    if (!isLoading && !pageViewFired) {
      fsiTabClickTrack(activeTab);
      fsiPageViewTrack(selectedPeriod);
      setPageViewFired(true);
    }
  }, [isLoading, pageViewFired, selectedPeriod, activeTab]);

  useEffect(() => {
    setPageViewFired(false);
  }, [selectedPeriod]);

  const inFsiRecords = useMemo(() => fsiData.inFsi ?? [], [fsiData.inFsi]);
  const outFsiRecords = useMemo(() => fsiData.outFsi ?? [], [fsiData.outFsi]);
  const downlines = useMemo(() => [...inFsiRecords, ...outFsiRecords], [inFsiRecords, outFsiRecords]);

  const tableColumns = useMemo(() => {
    return t("table.columns", {
      ns: "actionReportsFsiTracking",
      returnObjects: true,
      defaultValue: defaultContent.table.columns,
    }) as Record<string, string>;
  }, [t]);

  const sortableColumnValue = (record: FsiTrackingDownline, column: string): number => {
    switch (column) {
      case "ppvActual":
        return record.ppvActual?.value ?? 0;
      case "ppvNeeded":
        return record.ppvNeeded?.value ?? 0;
      case "vcsPvActual":
        return record.vcsPvActual?.value ?? 0;
      case "vcsPvNeeded":
        return record.vcsPvNeeded?.value ?? 0;
      case "vcsPercent":
        return record.vcsPercentActual?.value ?? 0;
      case "accumulatedVcsBv":
        return record.accumulatedVcsBv?.value ?? 0;
      case "exitMonth":
        return record.exitMonth ? Number(record.exitMonth) : 0;
      default:
        return 0;
    }
  };

  const activeRecords: FsiTrackingDownline[] = useMemo(() => {
    const tabRecords = activeTab === "inFsi" ? inFsiRecords : outFsiRecords;
    const tabSortState = columnSortState[activeTab];

    const column = Object.keys(tabSortState).find((key) => tabSortState[key]);
    if (!column) return tabRecords;

    const direction = tabSortState[column];
    return [...tabRecords].sort((a, b) => {
      const cmp =
        column === "aboName"
          ? a.displayName.localeCompare(b.displayName)
          : sortableColumnValue(a, column) - sortableColumnValue(b, column);
      return direction === "asc" ? cmp : -cmp;
    });
  }, [inFsiRecords, outFsiRecords, activeTab, columnSortState]);

  const reportData: BaseABOData[] = useMemo(
    () =>
      activeRecords.map((record) => ({
        aboName: record.displayName,
        aboNumber: record.abo,
        abo: record.abo,
        aff: record.aff,
        level: record.level,
        imageRefUrl: record.imageRefUrl,
        phoneNumber: record.phoneNumber,
        ppvActual: `${record.ppvActual?.formatted ?? "--"}/${record.ppvTarget?.formatted ?? "--"}`,
        ppvNeeded: record.ppvNeeded?.formatted ?? "--",
        vcsPvActual: `${record.vcsPvActual?.formatted ?? "--"}/${record.vcsPvTarget?.formatted ?? "--"}`,
        vcsPvNeeded: record.vcsPvNeeded?.formatted ?? "--",
        vcsPercent: record.vcsPercentActual?.formatted ?? "--",
        accumulatedVcsBv: record.accumulatedVcsBv?.formatted ?? "--",
        exitMonth: record.exitMonth ? getFullMonth(record.exitMonth, storedLocale()) : "--",
      })),
    [activeRecords],
  );

  const fieldConfig = useMemo(() => createFsiFieldConfig(tableColumns, activeTab), [tableColumns, activeTab]);
  const selectedKpis = SELECTED_KPIS[activeTab];

  const hasRestoredSortRef = useRef(false);
  useEffect(() => {
    if (hasRestoredSortRef.current || !savedPreferencesReady || !savedKpiPreferencesData) return;
    hasRestoredSortRef.current = true;
    const saved = getSavedSortPreferenceForView(FSI_SORT_VIEW_NAME, savedKpiPreferencesData);
    if (saved && isActiveSortValidForTab({ [saved.kpi]: saved.direction }, fieldConfig)) {
      setColumnSortState((prev) => getNextColumnSortState(prev, activeTab, saved.kpi, saved.direction));
    }
  }, [savedPreferencesReady, savedKpiPreferencesData, fieldConfig, activeTab]);

  const persistSortPreference = useCallback(
    (kpi: string, direction: SortDirection) => {
      const { preferenceDetails, value } = updateSavedSortPreferenceForView(
        FSI_SORT_VIEW_NAME,
        kpi,
        direction,
        savedKpiPreferencesData ?? { savedDataViews: {} },
      );
      dispatch(setSavedKpiPreferencesData(preferenceDetails));
      updateSavedKpiPreference({ viewId: FSI_SORT_VIEW_NAME, value })
        .unwrap()
        .catch((error: unknown) => console.error("Failed to update FSI sort preference:", error));
    },
    [savedKpiPreferencesData, updateSavedKpiPreference, dispatch],
  );

  const inFsiCount = fsiData.inFsiCount ?? 0;
  const outFsiCount = fsiData.outFsiCount ?? 0;

  const handleColumnHeaderClick = useCallback(
    (kpi: string) => {
      const currentDirection = columnSortState[activeTab][kpi];
      // Text columns default to A-Z first; numeric columns default to High to Low first, matching SortModal's ordering.
      const defaultDirection: SortDirection = kpi === "aboName" ? "asc" : "desc";
      const nextDirection: SortDirection = currentDirection
        ? currentDirection === "asc"
          ? "desc"
          : "asc"
        : defaultDirection;
      setColumnSortState((prev) => getNextColumnSortState(prev, activeTab, kpi, nextDirection));
      persistSortPreference(kpi, nextDirection);
      fsiSortColumnTrack(kpi, nextDirection);
    },
    [columnSortState, activeTab, persistSortPreference],
  );

  const handleSortModalApply = useCallback(
    (kpi: string, direction: SortDirection) => {
      setColumnSortState((prev) => getNextColumnSortState(prev, activeTab, kpi, direction));
      persistSortPreference(kpi, direction);
      fsiSortColumnTrack(kpi, direction);
    },
    [activeTab, persistSortPreference],
  );

  const sortableColumns = useMemo(
    () =>
      fieldConfig
        .filter((field) => field.sortable)
        .map((field) => ({
          kpi: field.kpi,
          title: field.title,
          type: field.kpi === "aboName" ? ("text" as const) : undefined,
        })),
    [fieldConfig],
  );

  const searchPool = useMemo(
    () => [
      ...inFsiRecords.map((record) => ({
        aff: record.aff,
        abo: record.abo,
        aboName: record.displayName,
        tab: "inFsi" as FsiTab,
      })),
      ...outFsiRecords.map((record) => ({
        aff: record.aff,
        abo: record.abo,
        aboName: record.displayName,
        tab: "outFsi" as FsiTab,
      })),
    ],
    [inFsiRecords, outFsiRecords],
  );

  const handleSearchCallback = useCallback<SearchCallback>(
    (text) =>
      searchPool
        .filter((entry) => entry.aboName.toLowerCase().includes(text.toLowerCase()) || entry.abo.includes(text))
        .map(
          (entry): SearchSelectItem => ({
            key: `${entry.aff}-${entry.abo}`,
            label: entry.aboName,
            sublabel: `${entry.abo} | ${entry.aff}`,
          }),
        ),
    [searchPool],
  );

  const handleSelectionCallback = useCallback<SelectionCallback>(
    (key) => {
      const found = searchPool.find((entry) => `${entry.aff}-${entry.abo}` === key);
      if (found) {
        setActiveTab(found.tab);
        setScrollToAbo({ aff: found.aff, abo: found.abo });
        setSearchText(found.aboName);
      }
      return { displayName: found?.aboName ?? "" };
    },
    [searchPool],
  );

  const handleClearSearch = useCallback(() => {
    setSearchText("");
    setScrollToAbo(undefined);
  }, []);

  const [fsiActionReportExport] = useFsiActionReportExportV2Mutation();

  const { user } = boot;
  const profile = configuration.profile;

  const exportAffAbos = useMemo(
    () =>
      activeRecords.map((record) => ({
        affAbo: `${record.aff}-${record.abo}`,
        level: record.level,
        sponsorAbo: "",
      })),
    [activeRecords],
  );

  const userIsoCountryCode = user.isoCountryCode;
  const userAff = user.aff;
  const userAbo = user.abo;
  const addressFormat = profile?.format;

  const customTriggerExport = useCallback(async () => {
    if (isExportDisabled) return;
    const affAbo = `${userAff}-${userAbo}`;
    const isoCountryCode = userIsoCountryCode || "";
    const { address, addressLinesDivider } = addressFormat || {};

    const textOverride = t("textOverride", {
      returnObjects: true,
      defaultValue: serverSideExportContent.textOverride,
      ns: "serverSideExport",
    }) as TextOverride;

    const exportFields = t("exportFields", {
      returnObjects: true,
      defaultValue: serverSideExportContent.exportFields,
      ns: "serverSideExport",
    }) as Record<string, string>;

    const startedBy = activeTab === "inFsi" ? MODULE_FSI_ACTION_REPORT_IN_FSI : MODULE_FSI_ACTION_REPORT_OUT_FSI;

    const basePayload = buildBaseExportPayload({
      moduleId: MODULE_FSI_ACTION_REPORT,
      reportAffAbo: affAbo,
      period: selectedPeriod,
      startedBy,
      countryCode: isoCountryCode,
      addressPattern: address || "",
      addressLineDivider: addressLinesDivider || "",
      selectedKpis,
      textOverride,
      exportFields,
    });

    await fsiActionReportExport({
      ...basePayload,
      affAbos: exportAffAbos,
    }).unwrap();
  }, [
    userIsoCountryCode,
    userAff,
    userAbo,
    addressFormat,
    selectedPeriod,
    t,
    fsiActionReportExport,
    exportAffAbos,
    activeTab,
    selectedKpis,
    isExportDisabled,
  ]);

  const notifyDownloadLinkOnClick = useCallback(() => {
    if (isExportDisabled) return;
    dispatchOpenExportV2ModalEvent(MODULE_FSI_ACTION_REPORT, true);
  }, [isExportDisabled]);

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="FsiAR__content">
          <ActionReportListViewLoader />
        </div>
      );
    }

    if (errorStatus) {
      return (
        <div className="FsiAR__error">
          <ErrorPage errorType={ERROR_TYPE.GENERIC_ERROR} iconType={IconType.SearchCriteria} />
        </div>
      );
    }

    if (activeRecords.length === 0) {
      return (
        <div className="FsiAR__noDownlines">
          <ErrorPage errorType={ERROR_TYPE.NO_DOWNLINES} iconType={IconType.SearchCriteria} />
        </div>
      );
    }

    return (
      <div className="FsiAR__content">
        <ActionReportListView
          selectedKpis={selectedKpis}
          reportId="fsiAR"
          reportData={reportData}
          fieldConfig={fieldConfig}
          enableWatchlist={true}
          showImageBlock={true}
          aboHeader="nameNumber"
          skipEnrichment={true}
          enrichWhatsApp={true}
          scrollToIndex={
            scrollToAbo
              ? reportData.findIndex((r) => r.aff === scrollToAbo.aff && r.abo === scrollToAbo.abo)
              : undefined
          }
          onProfileTrack={fsiProfileTrack}
          onWatchlistTrack={(isAdding) => (isAdding ? fsiAddToWatchlistTrack() : fsiRemoveFromWatchlistTrack())}
          onPhoneTrack={fsiPhoneIconTrack}
          onMessageTrack={fsiMessageIconTrack}
          onColumnHeaderClick={handleColumnHeaderClick}
          columnSortState={columnSortState[activeTab]}
        />
      </div>
    );
  };

  if (!isEligible && !isLoading) {
    return null;
  }

  const pageTitle = getCanonicalTitle({ id: "fsiActionReport" }, t, { symbol: disclaimerSymbol });

  return (
    <main className="FsiAR">
      <ActionReportHeader title={pageTitle} description={""} reportLength={downlines.length} />
      <ActionReportToolbar
        reportLength={activeRecords.length}
        isAllEnriched={true}
        search={{
          enabled: true,
          searchCallback: handleSearchCallback,
          selectionCallback: handleSelectionCallback,
          searchText: searchText,
          clearFunction: handleClearSearch,
          disabled: isLoading || searchPool.length === 0,
          onFocus: fsiSearchTrack,
        }}
        sortModal={{
          enabled: true,
          columns: sortableColumns,
          columnSortState: columnSortState[activeTab],
          onApply: handleSortModalApply,
          disabled: isLoading || activeRecords.length === 0,
        }}
        sortModalTrack={fsiSortButtonTrack}
        export={{
          moduleId: MODULE_FSI_ACTION_REPORT,
          exportAffAbos,
          selectedKpis,
          confirmationMessage:
            activeTab === "inFsi"
              ? t("export.descriptionInFsi", defaultContent.export.descriptionInFsi, {
                  symbol: disclaimerSymbol,
                }).toString()
              : t("export.descriptionOutFsi", defaultContent.export.descriptionOutFsi, {
                  symbol: disclaimerSymbol,
                }).toString(),
          exportType: MODULE_FSI_ACTION_REPORT,
          startedByLabels: {
            [MODULE_FSI_ACTION_REPORT_IN_FSI]: t("tabs.inFsi", defaultContent.tabs.inFsi).toString(),
            [MODULE_FSI_ACTION_REPORT_OUT_FSI]: t("tabs.outFsi", defaultContent.tabs.outFsi).toString(),
          },
          customTriggerExport,
        }}
        exportTrack={fsiExportTrack}
      />
      <div className="FsiAR__tabs" role="tablist">
        {isLoading ? (
          <div className="leaf-tab-views__loader">
            <LeafRectangleLoader isLoading={true} width="25rem" height="2rem" />
          </div>
        ) : (
          (["inFsi", "outFsi"] as FsiTab[]).map((tabKey) => {
            const isActive = activeTab === tabKey;
            const count = tabKey === "inFsi" ? inFsiCount : outFsiCount;
            return (
              <button
                key={tabKey}
                role="tab"
                aria-selected={isActive}
                className={`FsiAR__tab${isActive ? " FsiAR__tab--active" : ""}`}
                onClick={() => {
                  fsiTabClickTrack(tabKey);
                  setActiveTab(tabKey);
                  setScrollToAbo(undefined);
                }}
              >
                {`${t(`tabs.${tabKey}`, defaultContent.tabs[tabKey])} (${count})`}
              </button>
            );
          })
        )}
      </div>
      {renderContent()}
      <div>
        <BonusInfoFooter />
      </div>
      <ServerSideExportInProgressNotify />
      <ServerSideExportCompletedNotify
        moduleId={MODULE_FSI_ACTION_REPORT}
        notifyDownloadLinkOnClick={notifyDownloadLinkOnClick}
      />
    </main>
  );
}
